import React, { useEffect, useMemo, useState } from "react";
import "../../App.css";
import { useSpring, animated } from "@react-spring/web";
import {
  DownOutlined,
  ArrowLeftOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "../../i18n/useTranslation";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { createPaymentIntent } from "../../api/backend";
import StripeCheckoutForm from "./StripeCheckoutForm";
import { Link } from "react-router-dom";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function PaymentModal({ setIsOpen, onBack, onPay }) {
  const { t } = useTranslation();
  const { basket, basketTotalPrice, userProfile, currentRestaurant } = useAuth();

  const springs = useSpring({ from: { y: 600 }, to: { y: 0 } });

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevOverscroll = document.body.style.overscrollBehavior;

    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = prevOverflow || "";
      document.body.style.overscrollBehavior = prevOverscroll || "";
    };
  }, []);

  const [clientSecret, setClientSecret] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  const [serviceType, setServiceType] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState(
    userProfile?.address || ""
  );
  const [firstName, setFirstName] = useState(userProfile?.firstName || "");
  const [comment, setComment] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [tipPercent, setTipPercent] = useState(null);

  const isLocked = paymentLoading || !!clientSecret;
  const isDigitalProduct = currentRestaurant?.productType === "digital";
  const [email, setEmail] = useState(userProfile?.email || "");

  useEffect(() => {
    if (userProfile?.email) {
      setEmail(userProfile.email);
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile?.firstName) {
      setFirstName(userProfile.firstName);
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile?.address) {
      setDeliveryAddress(userProfile.address);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!currentRestaurant) return;

    if (isDigitalProduct) {
      setServiceType("digital");
      setTableNumber("");
      setComment("");
      setTipPercent(null);
      return;
    }

    const allowed = [];
    if (currentRestaurant.enableTable !== false) allowed.push("table");
    if (currentRestaurant.enableTakeaway !== false) allowed.push("takeaway");
    if (currentRestaurant.enableDelivery) allowed.push("delivery");

    if (!allowed.includes(serviceType)) {
      setServiceType(allowed[0] || "");
    }
  }, [currentRestaurant, serviceType, isDigitalProduct]);

  const tipOptions = useMemo(() => [10, 15, 20, 25], []);

  const subtotal = useMemo(
    () => Number(basketTotalPrice || 0),
    [basketTotalPrice]
  );

  const deliveryFee = useMemo(() => {
    if (isDigitalProduct) return 0;
    return serviceType === "delivery"
      ? Number(currentRestaurant?.deliveryFee || 0)
      : 0;
  }, [serviceType, currentRestaurant, isDigitalProduct]);

  const tipAmount = useMemo(() => {
    if (isDigitalProduct) return 0;
    return (subtotal * (tipPercent || 0)) / 100;
  }, [subtotal, tipPercent, isDigitalProduct]);

  const total = useMemo(
    () => subtotal + deliveryFee + tipAmount,
    [subtotal, deliveryFee, tipAmount]
  );

  const paymentLabel = t("card") || "Carte";

  const close = () => setIsOpen(false);

  const normalizeErrorMessage = (message) => {
    if (!message) return "Impossible d'initialiser le paiement";

    if (message.includes("Stock insuffisant")) {
      return message;
    }

    if (message.includes("Restaurant Fermé")) {
      return "Le restaurant est actuellement fermé";
    }

    if (message.includes("Product not found")) {
      return "Un produit du panier n'est plus disponible";
    }

    if (message.includes("quantity must be a positive integer")) {
      return "Une quantité du panier est invalide";
    }

    return message;
  };

  const handlePay = async (e) => {
    e.stopPropagation();
    setPaymentError("");
    setClientSecret("");
  
    if (!basket?.length) {
      setPaymentError("Votre panier est vide");
      return;
    }
  
    if (currentRestaurant?.isOpen === false) {
      setPaymentError("Le restaurant est actuellement fermé");
      return;
    }
  
    if (!currentRestaurant?.stripeAccountId) {
      setPaymentError("Compte Stripe restaurant introuvable");
      return;
    }
  
    if (!firstName.trim()) {
      setPaymentError("Prénom requis");
      return;
    }

    if (isDigitalProduct && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setPaymentError("Email valide requis pour recevoir le produit digital");
      return;
    }
  
    if (!isDigitalProduct && serviceType === "table" && !tableNumber) {
      setPaymentError("Numéro de table requis");
      return;
    }
  
    if (
      !isDigitalProduct &&
      serviceType === "delivery" &&
      !deliveryAddress.trim()
    ) {
      setPaymentError("Adresse de livraison requise");
      return;
    }
  
    try {
      setPaymentLoading(true);
  
      const payload = {
        restaurantId: currentRestaurant?.id,
        restaurantStripeAccount: currentRestaurant?.stripeAccountId,
        restaurantName: currentRestaurant?.name,
        restaurantAddress:
          currentRestaurant?.address || currentRestaurant?.adress || null,
        serviceType: isDigitalProduct ? "digital" : serviceType,
        customerEmail: isDigitalProduct ? email.trim() : userProfile?.email || null,
        tableNumber:
          !isDigitalProduct && serviceType === "table"
            ? tableNumber || null
            : null,
        deliveryAddress:
          !isDigitalProduct && serviceType === "delivery"
            ? deliveryAddress.trim() || null
            : null,
        tipPercent: isDigitalProduct ? null : tipPercent,
        firstName: firstName.trim() || null,
        customerPhone: userProfile?.phone || null,
        comment: isDigitalProduct ? null : comment.trim() || null,
        items: basket.map((it) => ({
          productId: it.product?.id,
          quantity: Number(it.quantity || 1),
          optionId: it.selections?.option?.id || null,
          supplementIds: (it.selections?.supplements || []).map((s) => s.id),
        })),
      };
  
      const data = await createPaymentIntent(payload);
      setClientSecret(data.clientSecret);
    } catch (err) {
      setPaymentError(normalizeErrorMessage(err.message));
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div className="product-modal-container">
      <animated.div
        className="product-modal-content modal-total payment-modal"
        style={{ ...springs }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-text-container modal-container payment-header">
          <ArrowLeftOutlined
            className="payment-back"
            onClick={() => (onBack ? onBack() : close())}
          />

          <p className="modal-text-header payment-title">
            {t("finalize_order") || "Finaliser la commande"}
          </p>
        </div>

        <div className="modal-text-container payment-body">
          <div className="payment-section">
            <p className="payment-section-title">
              {t("service_type") || "Type de service"}
            </p>

            <div className="payment-field">
              <select
                className="payment-input"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                disabled={isLocked || isDigitalProduct}
              >
                {isDigitalProduct ? (
                  <option value="digital">Digital</option>
                ) : (
                  <>
                    {currentRestaurant?.enableTable !== false && (
                      <option value="table">{t("at_table") || "À table"}</option>
                    )}

                    {currentRestaurant?.enableTakeaway !== false && (
                      <option value="takeaway">
                        {t("take_away") || "À emporter"}
                      </option>
                    )}

                    {currentRestaurant?.enableDelivery && (
                      <option value="delivery">Livraison à domicile</option>
                    )}
                  </>
                )}
              </select>
              <DownOutlined className="payment-chevron" />
            </div>

            {serviceType === "table" && !isDigitalProduct && (
              <div className="payment-field">
                <select
                  className="payment-input"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  disabled={isLocked}
                >
                  <option value="">
                    {t("table_number") || "Numéro de table"}
                  </option>
                  {Array.from({ length: 30 }).map((_, i) => (
                    <option key={i + 1} value={String(i + 1)}>
                      {(t("table") || "Table") + " " + (i + 1)}
                    </option>
                  ))}
                </select>
                <DownOutlined className="payment-chevron" />
              </div>
            )}

            {serviceType === "delivery" && !isDigitalProduct && (
              <div className="payment-field">
                <input
                  className="payment-input"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Adresse de livraison"
                  disabled={isLocked}
                />
              </div>
            )}

<div className="payment-field">
  <input
    className="payment-input"
    value={firstName}
    onChange={(e) => setFirstName(e.target.value)}
    placeholder={t("first_name") || "Prénom"}
    disabled={isLocked}
  />
</div>

{isDigitalProduct && (
  <div className="payment-field">
    <input
      className="payment-input"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder="Email"
      disabled={isLocked}
      type="email"
      required
    />
  </div>
)}
          </div>

          {!isDigitalProduct && (
            <>
              <div className="payment-divider" />

              <div className="payment-section">
                <div className="payment-row-between">
                  <p className="payment-section-title">
                    {t("tip") || "Pourboire"}
                  </p>
                  <p className="payment-tip-amount">
                    {tipAmount.toFixed(2)} €
                  </p>
                </div>

                <div className="payment-tip-grid">
                  {tipOptions.map((p) => {
                    const amount = (subtotal * p) / 100;
                    const active = tipPercent === p;

                    return (
                      <button
                        key={p}
                        type="button"
                        className={`payment-tip-pill ${active ? "is-active" : ""}`}
                        onClick={() =>
                          setTipPercent((prev) => (prev === p ? null : p))
                        }
                        disabled={isLocked}
                      >
                        <div className="payment-tip-pill-top">{p}%</div>
                        <div className="payment-tip-pill-bottom">
                          {amount.toFixed(2)} €
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="payment-field">
                  <input
                    className="payment-input"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={
                      t("kitchen_comment") ||
                      "Ajouter un commentaire pour la cuisine"
                    }
                    disabled={isLocked}
                  />
                </div>
              </div>
            </>
          )}

          <div className="payment-divider" />

          <div className="payment-section">
            <p className="payment-section-title">
              {t("payment_method") || "Mode de paiement"}
            </p>

            <div className="payment-field">
              <select
                disabled={isLocked}
                className="payment-input"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="card">{t("card") || "Carte"}</option>
              </select>
              <DownOutlined className="payment-chevron" />
            </div>
          </div>

          <div className="payment-divider" />

          <div className="payment-section">
            <p className="payment-section-title">{t("order") || "Commande"}</p>

            <div className="payment-order">
              {basket?.map((it, idx) => (
                <div key={it.key || idx} className="payment-order-item">
                  <div className="payment-order-left">
                    <div className="payment-order-name">
                      {(it.quantity || 1) > 1 ? `${it.quantity}× ` : ""}
                      {it.product?.name}
                    </div>

                    {it.selections?.option && (
                      <div className="payment-order-sub">
                        {it.selections.option.name}
                      </div>
                    )}

                    {!!it.selections?.supplements?.length && (
                      <div className="payment-order-sub">
                        {it.selections.supplements.map((s, i) => (
                          <div key={i}>{s.name}</div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="payment-order-right">
                    {(it.pricing?.totalPrice || 0).toFixed(2)} €
                  </div>
                </div>
              ))}
            </div>

            <div className="payment-divider soft" />

            {!isDigitalProduct && deliveryFee > 0 && (
              <div className="payment-row-between">
                <p>Frais de livraison</p>
                <p>{deliveryFee.toFixed(2)} €</p>
              </div>
            )}

            {!isDigitalProduct && tipPercent && (
              <div className="payment-row-between">
                <p>{t("tip") || "Pourboire"}</p>
                <p>{tipAmount.toFixed(2)} €</p>
              </div>
            )}

            <div className="payment-row-between total-row">
              <p className="payment-total-label">
                {t("total_amount") || "Montant total"}
              </p>
              <p className="payment-total-value">{total.toFixed(2)} €</p>
            </div>
          </div>

          {paymentError ? (
            <p className="auth-error" style={{ marginTop: 16 }}>
              {paymentError}
            </p>
          ) : null}

          {clientSecret && (
            <div
              className="payment-section"
              style={{ marginTop: 20, paddingBottom: 24 }}
            >
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <StripeCheckoutForm
                  onSuccess={(paymentIntent) => {
                    setPaymentError("");
                    onPay?.(paymentIntent);
                  }}
                />
              </Elements>

              <p
                className="auth-helper"
                style={{ marginTop: 14, fontSize: 13, lineHeight: 1.45 }}
              >
                En confirmant le paiement, vous acceptez les{" "}
                <Link to="/terms-of-sale" className="legal">
                  Conditions de vente
                </Link>{" "}
                et les{" "}
                <Link to="/terms-of-service" className="legal">
                  Conditions d’utilisation
                </Link>
                . Paiement sécurisé via Stripe.
              </p>
            </div>
          )}
        </div>

        {!clientSecret && (
          <div className="modal-close-container">
            <div
              className="modal-close-button"
              onClick={handlePay}
              style={{
                pointerEvents: paymentLoading ? "none" : "auto",
              }}
            >
              {paymentLoading ? (
                <LoadingOutlined spin />
              ) : (
                `${t("pay_with") || "Payer par"} ${paymentLabel} : ${total.toFixed(2)} €`
              )}
            </div>
          </div>
        )}
      </animated.div>
    </div>
  );
}